<script setup lang="ts">
import { computed } from 'vue';

interface EditorialHighlight {
  label: string;
  body: string;
}
interface EditorialEssential {
  icon: string;
  label: string;
  items: string;
}
interface EditorialAudience {
  icon?: string;
  persona: string;
  body: string;
}
interface EditorialResort {
  lead?: string;
  items: string[];
}
interface Editorial {
  hero: { title: string; intro: string | string[] };
  highlights: EditorialHighlight[];
  essentials: EditorialEssential[];
  resort?: EditorialResort;
  audiences: EditorialAudience[];
  cta?: string;
}

const props = defineProps<{
  // Accepts any content collection item (typed collection items have no
  // string index signature, so a plain `object` union member is required).
  doc?: { editorial?: Editorial } | object | null;
}>();

const { t } = useI18n();

const editorial = computed<Editorial | null>(() => {
  const ed = (props.doc as { editorial?: Editorial } | undefined)?.editorial;
  return ed && ed.hero ? ed : null;
});

const introParagraphs = computed<string[]>(() => {
  const i = editorial.value?.hero.intro;
  if (!i) return [];
  return Array.isArray(i) ? i : [i];
});

// Author-controlled YAML content; supports inline **bold** → <strong>.
// HTML-escaped first anyway (defense-in-depth for the v-html sink below).
function renderInlineMd(s: string): string {
  const escaped = s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return escaped.replace(
    /\*\*(.+?)\*\*/g,
    '<strong class="font-semibold text-slate-900 dark:text-white">$1</strong>',
  );
}
</script>

<template>
  <section v-if="editorial" class="apt-editorial mt-10 space-y-12 sm:space-y-14 md:space-y-20">
    <!-- Hero -->
    <header>
      <span aria-hidden="true" class="block h-px w-10 bg-amber-400" />
      <h2
        class="mt-4 font-display text-3xl italic leading-tight text-slate-900 sm:text-4xl md:text-5xl dark:text-white"
      >
        {{ editorial.hero.title }}
      </h2>
      <div
        class="mt-5 max-w-3xl space-y-4 font-body text-base leading-relaxed text-slate-700 sm:text-lg dark:text-slate-200"
      >
        <p v-for="(p, i) in introParagraphs" :key="i" v-html="renderInlineMd(p)" />
      </div>
    </header>

    <!-- Highlights — 2×2 grid of feature blocks -->
    <section aria-labelledby="apt-section-highlights">
      <h2
        id="apt-section-highlights"
        class="font-body text-[11px] uppercase tracking-[0.3em] text-amber-600 dark:text-amber-400"
      >
        {{ t('apartment.sections.highlights') }}
      </h2>
      <div class="mt-6 grid grid-cols-1 gap-x-10 gap-y-7 md:grid-cols-2">
        <article
          v-for="item in editorial.highlights"
          :key="item.label"
          class="border-l-2 border-amber-400/70 pl-5"
        >
          <h3
            class="font-display text-xl italic leading-tight text-slate-900 sm:text-2xl md:text-[1.75rem] dark:text-white"
          >
            {{ item.label }}
          </h3>
          <p class="mt-2 font-body text-[15px] md:text-base leading-relaxed text-slate-600 dark:text-slate-300">
            {{ item.body }}
          </p>
        </article>
      </div>
    </section>

    <!-- Essentials — 4-column icon grid -->
    <section aria-labelledby="apt-section-essentials">
      <h2
        id="apt-section-essentials"
        class="font-body text-[11px] uppercase tracking-[0.3em] text-amber-600 dark:text-amber-400"
      >
        {{ t('apartment.sections.essentials') }}
      </h2>
      <div
        class="mt-6 grid grid-cols-2 gap-x-6 gap-y-8 md:gap-x-6 lg:grid-cols-4 lg:gap-x-8"
      >
        <div v-for="group in editorial.essentials" :key="group.label">
          <span
            class="inline-flex size-9 items-center justify-center rounded-full bg-amber-50 text-amber-600 dark:bg-amber-500/15 dark:text-amber-300"
          >
            <Icon :name="`lucide:${group.icon}`" class="size-4" aria-hidden="true" />
          </span>
          <h3
            class="mt-3 font-body text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-900 dark:text-white"
          >
            {{ group.label }}
          </h3>
          <p class="mt-1.5 font-body text-sm md:text-base leading-relaxed text-slate-600 dark:text-slate-400">
            {{ group.items }}
          </p>
        </div>
      </div>
    </section>

    <!-- Resort & grounds — minimal horizontal row with amber rule above each item -->
    <section v-if="editorial.resort" aria-labelledby="apt-section-resort">
      <h2
        id="apt-section-resort"
        class="font-body text-[11px] uppercase tracking-[0.3em] text-amber-600 dark:text-amber-400"
      >
        {{ t('apartment.sections.resort') }}
      </h2>
      <p
        v-if="editorial.resort.lead"
        class="mt-4 max-w-3xl font-display text-lg italic leading-snug text-slate-700 md:text-xl dark:text-slate-200"
      >
        {{ editorial.resort.lead }}
      </p>
      <div
        class="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3"
      >
        <div v-for="(item, i) in editorial.resort.items" :key="i">
          <span aria-hidden="true" class="block h-px w-6 bg-amber-400/80" />
          <p class="mt-3 font-body text-[15px] md:text-base leading-relaxed text-slate-700 dark:text-slate-300">
            {{ item }}
          </p>
        </div>
      </div>
    </section>

    <!-- Audiences — chip-cards with icon + persona italic -->
    <section aria-labelledby="apt-section-audiences">
      <h2
        id="apt-section-audiences"
        class="font-body text-[11px] uppercase tracking-[0.3em] text-amber-600 dark:text-amber-400"
      >
        {{ t('apartment.sections.audiences') }}
      </h2>
      <div class="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 md:gap-4 lg:grid-cols-4">
        <article
          v-for="aud in editorial.audiences"
          :key="aud.persona"
          class="rounded-lg border border-slate-200/70 bg-white/40 p-4 dark:border-slate-700/50 dark:bg-slate-800/30"
        >
          <div class="flex items-center gap-3">
            <span
              v-if="aud.icon"
              class="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-amber-50 text-amber-600 dark:bg-amber-500/15 dark:text-amber-300"
            >
              <Icon :name="`lucide:${aud.icon}`" class="size-4" aria-hidden="true" />
            </span>
            <h3
              class="font-display text-base italic leading-tight text-slate-900 sm:text-lg dark:text-white"
            >
              {{ aud.persona }}
            </h3>
          </div>
          <p
            class="mt-2 font-body text-sm leading-snug text-slate-600 dark:text-slate-400"
          >
            {{ aud.body }}
          </p>
        </article>
      </div>
    </section>

    <!-- Booking conditions — info card driven by i18n (identical across apartments) -->
    <section aria-labelledby="apt-section-booking">
      <h2
        id="apt-section-booking"
        class="font-body text-[11px] uppercase tracking-[0.3em] text-amber-600 dark:text-amber-400"
      >
        {{ t('apartment.sections.booking') }}
      </h2>
      <div
        class="mt-6 overflow-hidden rounded-2xl border border-amber-400/40 bg-gradient-to-br from-amber-400/10 via-amber-400/5 to-transparent p-6 md:p-8"
      >
        <p class="font-display text-lg italic text-slate-900 sm:text-xl md:text-2xl dark:text-white">
          {{ t('apartment.booking.lead') }}
        </p>
        <ul class="mt-5 space-y-4 font-body text-[15px] md:text-base">
          <li class="flex items-start gap-3">
            <Icon
              name="lucide:credit-card"
              class="mt-0.5 size-5 shrink-0 text-amber-500"
              aria-hidden="true"
            />
            <p class="text-slate-700 dark:text-slate-200">
              <span class="font-semibold text-slate-900 dark:text-white"
                >{{ t('apartment.booking.prepayment.label') }}.</span
              >
              {{ ' ' + t('apartment.booking.prepayment.body') }}
            </p>
          </li>
          <li class="flex items-start gap-3">
            <Icon
              name="lucide:gift"
              class="mt-0.5 size-5 shrink-0 text-amber-500"
              aria-hidden="true"
            />
            <p class="text-slate-700 dark:text-slate-200">
              <span class="font-semibold text-slate-900 dark:text-white"
                >{{ t('apartment.booking.weekly.label') }}.</span
              >
              {{ ' ' + t('apartment.booking.weekly.body') }}
            </p>
          </li>
          <li class="flex items-start gap-3">
            <Icon
              name="lucide:sparkles"
              class="mt-0.5 size-5 shrink-0 text-amber-500"
              aria-hidden="true"
            />
            <p class="text-slate-700 dark:text-slate-200">
              <span class="font-semibold text-slate-900 dark:text-white"
                >{{ t('apartment.booking.cleaning.label') }}.</span
              >
              {{ ' ' + t('apartment.booking.cleaning.free') }}
            </p>
          </li>
        </ul>
      </div>
    </section>

    <!-- CTA — highlighted closing line (optional) -->
    <aside
      v-if="editorial.cta"
      class="rounded-xl border-l-4 border-amber-400 bg-gradient-to-r from-amber-400/10 to-transparent px-5 py-5 md:px-7 md:py-6"
    >
      <p
        class="font-display text-xl italic leading-snug text-slate-900 sm:text-2xl md:text-3xl dark:text-white"
      >
        {{ editorial.cta }}
      </p>
    </aside>
  </section>

  <!-- Fallback for apartments without `editorial` frontmatter — render raw markdown body -->
  <section v-else class="mt-10">
    <ContentRenderer :value="(props.doc ?? {}) as Record<string, unknown>" class="apt-editorial-fallback" />
  </section>
</template>

<style>
@reference "~/assets/css/main.css";

.apt-editorial-fallback {
  @apply font-body text-base leading-relaxed text-slate-700 dark:text-slate-200;
}
.apt-editorial-fallback p {
  @apply mt-5;
}
.apt-editorial-fallback p:first-child {
  @apply mt-0;
}
.apt-editorial-fallback strong {
  @apply font-semibold text-slate-900 dark:text-white;
}
.apt-editorial-fallback h1 {
  @apply font-display italic text-3xl text-slate-900 mt-0 mb-4 md:text-4xl dark:text-white;
}
.apt-editorial-fallback h1::before {
  content: "";
  @apply mb-4 block h-px w-8 bg-amber-400;
}
.apt-editorial-fallback h2,
.apt-editorial-fallback h3 {
  @apply font-display italic text-xl text-slate-900 mt-8 mb-3 flex items-center gap-2.5 md:text-2xl dark:text-white;
}
.apt-editorial-fallback h2::before,
.apt-editorial-fallback h3::before {
  content: "";
  @apply inline-block h-1.5 w-1.5 rotate-45 bg-amber-400;
}
.apt-editorial-fallback hr {
  @apply my-8 h-px border-0 bg-gradient-to-r from-transparent via-amber-400/40 to-transparent;
}
.apt-editorial-fallback ul {
  @apply mt-3 list-none space-y-1.5 pl-0;
}
.apt-editorial-fallback ul li {
  @apply relative pl-5 text-slate-700 dark:text-slate-200;
}
.apt-editorial-fallback ul li::before {
  content: "";
  @apply absolute left-0 top-[0.6em] h-1.5 w-1.5 rounded-full bg-amber-500/80;
}
</style>
